//Copyright Open Trade Technology Ltd. 2023. All rights reserved.

import { timestampToUTCDateString, timestampToUTCTimeString, ERC20Wei } from '@shared/conversions'
import { yupEthAddrRequired, yupTxHashRequired, yupErc20WeiRequired } from '@shared/Enums'
import { toFieldMetaData, StringTypeEnum, ReportingColumnVisibilityEnum, EthAddr, TxHash } from '@shared/SchemaTypes'
import { EventTypeForTemplate, EventNameForTemplate } from '@shared/templateApx'
import { ZeroAddress } from 'ethers'
import * as yup from 'yup'
import { InferType } from 'yup'

const SierraEventSchemaValues = {
  id: yup
    .string()
    .label('PoolEventId ')
    .required()
    .meta(toFieldMetaData({ propName: 'id', description: 'ID of event ', stringType: StringTypeEnum.ID })),
  poolAddr: yupEthAddrRequired.label('Vault Address').meta(
    toFieldMetaData({
      propName: 'poolAddr',

      description: 'The smart-contract address of the loan vault. ',
      stringType: StringTypeEnum.ETH_ADDR,
    })
  ),
  poolName: yup
    .string()
    .label('Vault Name')
    .default(' ')
    .required()
    .meta(toFieldMetaData({ propName: 'poolName', description: 'Name of the Loan Vault' })),

  eventType: yup
    .mixed<EventTypeForTemplate>()
    .oneOf(Object.values(EventTypeForTemplate))
    .defined()
    .label('Event Label')
    .default(EventTypeForTemplate.Undefined)
    .meta(toFieldMetaData({ propName: 'eventType', description: 'Name of Event' })),

  eventName: yup
    .mixed<EventNameForTemplate>()
    .oneOf(Object.values(EventNameForTemplate))
    .defined()
    .label('Event Name')
    .default(EventNameForTemplate.Undefined)
    .meta(
      toFieldMetaData({
        propName: 'eventName',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 2,
        description: 'Name of Event',
      })
    ),

  currency: yup
    .string()
    .label('Currency Symbol')
    .default(' ')
    .required()
    .meta(toFieldMetaData({ propName: 'currency', description: 'currency' })),
  symbol: yup
    .string()
    .label('Token Symbol')
    .default(' ')
    .required()
    .meta(toFieldMetaData({ propName: 'symbol', description: 'Symbol for Vault Tokens', example: 'OTT' })),

  fromAddr: yupEthAddrRequired
    .label('From Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'fromAddr',
        description: 'The source address . ',
        stringType: StringTypeEnum.ETH_ADDR,
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 4,
      })
    ),

  toAddr: yupEthAddrRequired
    .label('To Address')
    .required()
    .default(ZeroAddress)
    .meta(
      toFieldMetaData({
        propName: 'toAddr',
        description: 'The destination address . ',
        stringType: StringTypeEnum.ETH_ADDR,
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 5,
      })
    ),

  blockNumber: yup
    .number()
    .label('Block Number')
    .required()
    .meta(toFieldMetaData({ propName: 'blockNumber', description: 'Block number of the data' })),

  transactionHash: yupTxHashRequired
    .required()
    .label('Transaction Hash')
    .meta(
      toFieldMetaData({
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 1,
        propName: 'transactionHash',
        stringType: StringTypeEnum.TX_HASH,
      })
    ),
  ageString: yup
    .string()
    .required()
    .label('Age')
    .default((options) => {
      return ' '
    })
    .meta(
      toFieldMetaData({
        propName: 'timestampDateString',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 3.1,
        description: 'Timestamp Date of the data',
      })
    ),
  timestamp: yup
    .number()
    .label('Timestamp')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'timestamp',
        // columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        // columnOrder: 3,
        description: 'Timestamp of the data',
        stringType: StringTypeEnum.ETH_DATE_TIME,
      })
    ),
  timestampDateString: yup
    .string()
    .required()
    .label('Timestamp Date')
    .default((options) => {
      if (options?.context?.poolEventCTO) {
        return timestampToUTCDateString(options.context.poolEventCTO.timestamp)
      }
      return ' '
    })
    .meta(
      toFieldMetaData({
        propName: 'timestampDateString',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 3.1,
        description: 'Timestamp Date of the data',
      })
    ),
  timestampString: yup
    .string()
    .required()
    .default((options) => {
      if (options?.context?.poolEventCTO) {
        return timestampToUTCTimeString(options.context.poolEventCTO.timestamp)
      }
      return ' '
    })
    .label('Timestamp Date Time')
    .meta(
      toFieldMetaData({
        propName: 'timestampString',
        description: 'Timestamp of the data',
      })
    ),

  chainId: yup
    .number()
    .required()
    .label('Chain Id')
    .meta(toFieldMetaData({ propName: 'chainId', example: '5', description: 'ChainId of the Data' })),

  liquidityTokenSymbol: yup
    .string()
    .label('Liquidity Token Symbol')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'liquidityTokenSymbol',

        description: 'liquidityTokenSymbol',
      })
    ),

  assets: yupErc20WeiRequired
    .label('Value')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'assets',
        description: 'Value',
        columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
        columnOrder: 6,
        stringType: StringTypeEnum.STABLE_COINS,
      })
    ),

  shares: yupErc20WeiRequired
    .label('Vault Tokens')
    .required()
    .meta(
      toFieldMetaData({
        propName: 'shares',
        description: 'shares',

        stringType: StringTypeEnum.PPT_SHARES,
      })
    ),

  fees: yupErc20WeiRequired.label('fees').meta(
    toFieldMetaData({
      propName: 'Fees',
      description: 'fees',
      columnVisibility: ReportingColumnVisibilityEnum.DefaultVisible,
      columnOrder: 7,
      stringType: StringTypeEnum.STABLE_COINS,
    })
  ),
}

export const SierraEventSchema = yup.object(SierraEventSchemaValues)
// .label('PoolEventCTO')
// .meta(toFieldMetaData({ propName: 'PoolEventCTO', description: 'Vault Event' }))

export interface SierraEventCTO {
  id: string
  poolAddr: EthAddr
  fromAddr: EthAddr
  toAddr: EthAddr
  poolName: string

  eventType: EventTypeForTemplate
  eventName: EventNameForTemplate
  currency: string
  symbol: string

  blockNumber: number
  transactionHash: TxHash
  ageString: string
  timestamp: number
  timestampDateString: string
  timestampString: string
  liquidityTokenSymbol: string

  chainId: number

  assets: ERC20Wei
  shares: ERC20Wei
  fees: ERC20Wei
}

type SierraEventCTOInfer = InferType<typeof SierraEventSchema>

export const _testASierraEvent: SierraEventCTO = {} as unknown as SierraEventCTOInfer
export const _testBSierraEvent: SierraEventCTOInfer = {} as unknown as SierraEventCTO
